import { useEffect, useMemo, useState } from "react";
import { api } from "./api/api";
import generateManifestListPdf from "./generateManifestListPdf";
import generateAirCargoDetailsPdf from "./generateAirCargoDetailsPdf";
import generateBolsStatusByFlightPdf from "./generateBolsStatusByFlightPdf";
import { formatNumber } from "./utils/numberFormat";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "./assets/logo.png";
import generateDeliveryOrderPdf from "./generateDeliveryOrderPdf";
import generateReceiptPdf from "./generateReceiptPdf";

const formatDateToYMD_forReprint = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return "";
  return dateString.slice(0, 10);
};

function  generateInvoicePdfForReprint({ header, invoice, recipientName, deliveryDate, days, discount = 0, d_no }) {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoWidth = 100;
  const logoHeight = 20;
  const logoX = (pageWidth - logoWidth) / 2;
  const logoY = 5;
  doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);
  let y = logoY + logoHeight + 20;
  doc.setFontSize(16);
  doc.text("Storage Charges Invoice", pageWidth / 2, y, { align: "center" });
  doc.setFontSize(11);
  y += 20;
  doc.text(`Voyage No.: ${header.voyage_number || ""}`, 80, y);
  doc.text(`Arrival Date: ${formatDateToYMD_forReprint(header.date_of_arrival) || ""}`, 150, y);
  
  if (d_no) {
    doc.text(`Invoice No: ${d_no}`, 14, y);
  }

  y += 6;
  doc.text(`Days of Storage: ${days}`, 150, y);
  y += 6;
  const consigneeText = `Consignee / Receiver: ${recipientName || ""}`;
  const consigneeLines = doc.splitTextToSize(consigneeText, 130);
  doc.text(consigneeLines, 14, y);
  doc.text(`Delivery Date: ${formatDateToYMD_forReprint(deliveryDate) || ""}`, 150, y);

  if (consigneeLines.length > 1) {
    const lineHeight = doc.getLineHeight() / doc.internal.scaleFactor;
    y += (consigneeLines.length - 1) * lineHeight;
  }

  if (invoice.service) {
    y += 6;
    doc.text(`Service: ${invoice.service.name}`, 14, y);
    doc.text(`Rate per day: ${formatNumber(invoice.service.price, 2)}`, 150, y);
  }
  const finalAmount = invoice.total - discount;
  const tableBody = invoice.lines.map(({ bol, unitPrice, qty, weight, amount }) => [bol.bol_reference || "", bol.goods_description || "", bol.consignee_name || "", formatNumber(qty || 0, 0), formatNumber(weight || 0, 2), formatNumber(unitPrice || 0, 2), days, formatNumber(finalAmount, 2)]);
  autoTable(doc, { startY: y + 20, head: [["AWB Ref.", "Goods Description", "Consignee", "Packages", "Weight", "Rate/day", "Days", "Total Amount"]], body: tableBody, styles: { fontSize: 8 }, headStyles: { fillColor: [66, 165, 245], textColor: 255 } });
  const firstBol = invoice.lines[0]?.bol;
  const fileName = `Invoice_${header.id}_${firstBol?.bol_reference || 'unknown'}.pdf`;
  doc.save(fileName);
}

function readPerms() {
  try {
    return JSON.parse(localStorage.getItem("permissions") || "[]");
  } catch {
    return [];
  }
}

function has(perms, code) {
  return Array.isArray(perms) && perms.includes(code);
}

export default function ReportsScreen({ permissions: permsProp }) {
  // ✅ Hooks أولاً (بدون أي return قبلها)
  const permissions = permsProp || readPerms();
  const canView = useMemo(() => has(permissions, "USERS_ADMIN"), [permissions]);

  // Tabs
  const [tab, setTab] = useState("manifests"); // "manifests" | "stock" | "financial" | "delivered" | "reprint"

  // Shared Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Flights
  const [flights, setFlights] = useState([]);
  const [flightQuery, setFlightQuery] = useState(""); // للفلترة داخل القائمة
  const [flightNo, setFlightNo] = useState("");

  // Manifest
  const [manifestId, setManifestId] = useState("");
  const [manifests, setManifests] = useState([]);

  // Stock
  const [stockQuery, setStockQuery] = useState("");
  const [stockItems, setStockItems] = useState([]);
  const [stockSummary, setStockSummary] = useState(null);
  const [stockMoves, setStockMoves] = useState([]);
  const [manifestFilterId, setManifestFilterId] = useState(""); // For stock movements
  const [stockByService, setStockByService] = useState(null);
  const [manifestDeclared, setManifestDeclared] = useState([]);
  const [manifestDeclaredVisible, setManifestDeclaredVisible] = useState(false);

  // Financial
  const [financialReport, setFinancialReport] = useState(null);
  const [deliveredReport, setDeliveredReport] = useState(null);
  const [deliveredDetailed, setDeliveredDetailed] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // Reprint state
  const [reprintQuery, setReprintQuery] = useState("");
  const [reprintResult, setReprintResult] = useState(null);

  // Split stock movements into IN and OUT
  const inMoves = useMemo(() => stockMoves.filter((m) => m.move_type === "IN"), [stockMoves]);
  const outMoves = useMemo(() => stockMoves.filter((m) => m.move_type === "OUT"), [stockMoves]);

  // Hide manifest report automatically when switching to Warehouse tab
  useEffect(() => {
    if (tab === "stock") setManifestDeclaredVisible(false);
  }, [tab]);

  // ✅ جلب قائمة المنافيست (للمساعدة لو احتجتها)
  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await api.get("/manifests/list");
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setManifests(list);
      } catch {
        setManifests([]);
      }
    })();
  }, [canView]);

  // ✅ جلب أرقام الرحلات
  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await api.get(`/reports/flights${qs({ from: dateFrom, to: dateTo })}`);
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        // لو الـ API يرجع objects بدل strings:
        const normalized = list.map((x) => (typeof x === "string" ? x : x?.flight_no || x?.flightNo || "")).filter(Boolean);
        setFlights(normalized);
      } catch {
        setFlights([]);
      }
    })();
  }, [canView, dateFrom, dateTo]);

  // ✅ بعد كل الـ Hooks نعمل شرط الصلاحية
  if (!canView) {
    return (
      <div className="card">
        <h3>Reports</h3>
        <div className="message error">  you don't have permission</div>
      </div>
    );
  }

  // فلترة قائمة الرحلات داخل datalist
  const filteredFlights = (Array.isArray(flights) ? flights : []).filter((f) =>
    String(f).toLowerCase().includes(String(flightQuery || "").toLowerCase())
  );

  const qs = (obj) => {
    const p = new URLSearchParams();
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== "") p.set(k, String(v));
    });
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  // ===== Manifest exports =====
  const exportManifestList = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/manifest-list${qs({ from: dateFrom, to: dateTo, flightNo: flightNo })}`);
      generateManifestListPdf(res || [], { from: dateFrom, to: dateTo });
      setMsg("  generated Manifest List ✅");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "  failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const loadManifestDeclaredVsActual = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/manifest-declared-vs-actual${qs({ from: dateFrom, to: dateTo, flightNo: flightNo })}`);
      setManifestDeclared(Array.isArray(res) ? res : []);
      setMsg("  loaded Manifest Declared vs Actual ✅");
    } catch (e) {
      console.error(e);
      setManifestDeclared([]);
      setMsg(e?.message || "  failed to load report");
    } finally {
      setLoading(false);
    }
  };

const exportAirCargoByFlight = async () => {
  const f = String(flightNo || "").trim();
  if (!f) {
    setMsg("Please select a valid Flight No");
    return;
  }
  try {
    setLoading(true);
    setMsg("");
    const res = await api.get(`/reports/air-cargo-by-flight${qs({ flightNo: f, from: dateFrom, to: dateTo })}`);
    generateAirCargoDetailsPdf(res);
    setMsg("  generated Air Cargo report by flight ✅");
  } catch (e) {
    console.error(e);
    setMsg(e?.message || "  failed to generate report");
  } finally {
    setLoading(false);
  }
};


const exportBolsStatusByFlight = async () => {
  const f = String(flightNo || "").trim();
  if (!f) {
    setMsg("Please select a valid Flight No");
    return;
  }
  try {
    setLoading(true);
    setMsg("");
    const res = await api.get(`/reports/bols-status-by-flight${qs({ flightNo: f, from: dateFrom, to: dateTo })}`);
    generateBolsStatusByFlightPdf(res);
    setMsg("BOL Status report generated ✅");
  } catch (e) {
    console.error(e);
    setMsg(e?.message || "Failed to generate report");
  } finally {
    setLoading(false);
  }
};

// const loadFinancialReport = async () => {
//     try {
//       setLoading(true);
//       setMsg("");
//       const res = await api.get(`/reports/financial-by-operator${qs({ fromDate: dateFrom, toDate: dateTo })}`);
//       setFinancialReport(res);
//       setMsg("Financial report loaded successfully ✅");
//     } catch (e) {
//       console.error(e);
//       setFinancialReport(null);
//       setMsg(e?.message || "Failed to load financial report");
//     } finally {
//       setLoading(false);
//     }
//   };

const loadFinancialReport = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/stock-value-by-operator${qs({ fromDate: dateFrom, toDate: dateTo })}`);
      setFinancialReport(res);
      setMsg("Financial report loaded successfully ✅");
    } catch (e) {
      console.error(e);
      setFinancialReport(null);
      setMsg(e?.message || "Failed to load financial report");
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveredReport = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/delivered-stock-by-operator${qs({ fromDate: dateFrom, toDate: dateTo })}`);
      setDeliveredReport(res);
      setMsg("Delivered report loaded successfully ✅");
    } catch (e) {
      console.error(e);
      setDeliveredReport(null);
      setMsg(e?.message || "Failed to load delivered report");
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveredDetailed = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/delivered-stock-detailed${qs({ from: dateFrom, to: dateTo })}`);
      setDeliveredDetailed(Array.isArray(res) ? res : []);
      setMsg("Detailed Delivered report loaded successfully ✅");
    } catch (e) {
      console.error(e);
      setDeliveredDetailed(null);
      setMsg(e?.message || "Failed to load detailed delivered report");
    } finally {
      setLoading(false);
    }
  };

  // ===== Stock loaders =====
  const loadStock = async () => {
    try {
      setLoading(true);
      setMsg("");
      const [items, summary, moves] = await Promise.all([
        api.get(`/reports/stock${qs({ q: stockQuery })}`),
        api.get(`/reports/stock-summary${qs({ from: dateFrom, to: dateTo })}`),
        api.get(`/reports/stock-movements${qs({ from: dateFrom, to: dateTo, ref: stockQuery || undefined })}`),
      ]);
      setStockItems(Array.isArray(items) ? items : []);
      setStockSummary(summary || null);
      setStockMoves(Array.isArray(moves) ? moves : []);
      setMsg("  updated Warehouse report ✅");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "  failed to fetch Warehouse report");
    } finally {
      setLoading(false);
    }
  };

  const loadStockByService = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/stock-by-service${qs({ q: stockQuery })}`);
      // res = { items: [...], summary: [...] }
      setStockByService(res || null);
      setMsg("  updated Stock By Service report ✅");
    } catch (e) {
      console.error(e);
      setStockByService(null);
      setMsg(e?.message || "  failed to fetch Stock By Service report");
    } finally {
      setLoading(false);
    }
  };

  const loadStockSummary = async () => {
    try {
      setLoading(true);
      setMsg("");
      const s = await api.get(`/reports/stock-summary${qs({ from: dateFrom, to: dateTo })}`);
      setStockSummary(s || null);
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "  failed to fetch stock summary");
      setStockSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const loadStockMoves = async () => {
    try {
      setLoading(true);
      setMsg("");
      const rows = await api.get(`/reports/stock-movements${qs({ from: dateFrom, to: dateTo, ref: stockQuery, manifestId: manifestFilterId })}`);
      setStockMoves(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "  failed to fetch stock movements");
      setStockMoves([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReprintSearch = async () => {
    if (!reprintQuery.trim()) return;
    setLoading(true);
    setMsg("");
    setReprintResult(null);
    try {
      const res = await api.get(`/bols/for-reprint/${encodeURIComponent(reprintQuery.trim())}`);
      setReprintResult(res);
      setMsg("AWB data loaded successfully.");
    } catch (e) {
      console.error(e);
      setReprintResult(null);
      setMsg(e?.message || "Failed to find AWB.");
    } finally {
      setLoading(false);
    }
  };

  const handleReprintDO = (data) => {
    if (!data || !data.bol || !data.header) {
      setMsg("Cannot print, data is missing.");
      return;
    }
    generateDeliveryOrderPdf({ bol: data.bol, header: data.header });
    generateReceiptPdf({ bol: data.bol });
    setMsg("DO and Receipt re-printed successfully.");
  };

  const handleReprintInvoice = (data) => {
    if (!data || !data.bol || !data.header || !data.service) {
      setMsg("Cannot print invoice, data is missing (BOL, Header, or Service).");
      return;
    }
    const { bol, header, service } = data;

    const arrivalDate = new Date(header.date_of_arrival);
    const deliveryD = new Date(bol.delivery_date);

    // Handle potential invalid dates
    if (isNaN(arrivalDate.getTime()) || isNaN(deliveryD.getTime())) {
        setMsg("Invalid arrival or delivery date.");
        return;
    }

    const diffMs = deliveryD.getTime() - arrivalDate.getTime();
    let days = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
    if (bol.day === 1) {
      days -= 1;
    }
    days = Math.max(1, days);

    const totalBeforeDiscount = Number(bol.invoice_amount || 0);
    const discountAmount = Number(bol.discount || 0);

    const invoice = {
      service: service,
      total: totalBeforeDiscount,
      lines: [{ bol: bol, unitPrice: service.price, qty: bol.number_of_packages, weight: bol.gross_mass, amount: totalBeforeDiscount }],
    };

    generateInvoicePdfForReprint({
      header: header,
      invoice: invoice,
      recipientName: bol.delivered_to,
      deliveryDate: bol.delivery_date,
      days: days,
      discount: discountAmount,
      d_no: bol.d_no,
    });
    setMsg("Invoice re-printed successfully.");
  };

  return (
    <div className="screen screen-rtl">
      <div className="screen-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Reports</h2>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => setTab("manifests")}
  style={{
    backgroundColor: tab === "manifests" ? "#1e88e5" : "#f5f5f5",
    color: tab === "manifests" ? "#fff" : "#333",
    border: "1px solid #1e88e5",
    padding: "8px 14px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
  }}
>
  Manifest Reports
</button>

<button
  type="button"
  onClick={() => {
    setManifestDeclaredVisible(false);
    setTab("stock");
  }}
  style={{
    backgroundColor: tab === "stock" ? "#1e88e5" : "#f5f5f5",
    color: tab === "stock" ? "#fff" : "#333",
    border: "1px solid #1e88e5",
    padding: "8px 14px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
  }}
>
  Warehouse Reports
</button>

<button
  type="button"
  onClick={() => setTab("financial")}
  style={{
    backgroundColor: tab === "financial" ? "#1e88e5" : "#f5f5f5",
    color: tab === "financial" ? "#fff" : "#333",
    border: "1px solid #1e88e5",
    padding: "8px 14px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
  }}
>
  Financial Reports
</button>

<button
  type="button"
  onClick={() => setTab("delivered")}
  style={{
    backgroundColor: tab === "delivered" ? "#1e88e5" : "#f5f5f5",
    color: tab === "delivered" ? "#fff" : "#333",
    border: "1px solid #1e88e5",
    padding: "8px 14px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
  }}
>
  Delivered Reports
</button>

<button
  type="button"
  onClick={() => setTab("reprint")}
  style={{
    backgroundColor: tab === "reprint" ? "#1e88e5" : "#f5f5f5",
    color: tab === "reprint" ? "#fff" : "#333",
    border: "1px solid #1e88e5",
    padding: "8px 14px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
  }}
>
  Re-print
</button>


        </div>
      </div>

      {/* Shared filters */}
      <div className="card">
        <h3>Filters</h3>
        <div className="grid grid-auto">
          <div className="field">
            <label>From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          {tab === "manifests" && (
<div className="field">
  <label>Flight No (Air Cargo Report)</label>
  <input
    value={flightNo}
    onChange={(e) => setFlightNo(e.target.value)}
    placeholder="مثال: 8V601"
    list="flightNos"
  />
  <datalist id="flightNos">
    {(Array.isArray(flights) ? flights : []).slice(0, 500).map((f) => (
      <option key={f} value={f}>{f}</option>
    ))}
  </datalist>
</div>

          )}

          {tab === "stock" && (
            <div className="field">
              <label>Filter by AWB</label>
              <input
                value={stockQuery}
                onChange={(e) => setStockQuery(e.target.value)}
                placeholder="example: 2569"
              />
            </div>
          )}

          {tab === "reprint" && (
            <div className="field">
              <label>Search by AWB to Re-print</label>
              <input
                value={reprintQuery}
                onChange={(e) => setReprintQuery(e.target.value)}
                placeholder="Enter AWB reference..."
              />
            </div>
          )}
        </div>

        {msg && (
          <div className={msg.includes("failed") ? "message error" : "message success"} style={{ marginTop: 10 }}>
            {msg}
          </div>
        )}
      </div>

      {/* ===== Tab: Manifests ===== */}
      {tab === "manifests" && (
        <div className="card">
          <h3>Manifest Reports (Export PDF)</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={exportManifestList} disabled={loading}>
              {loading ? "..." : "Print/Export PDF - Manifest List"}
            </button>
<button type="button" onClick={exportAirCargoByFlight} disabled={loading}>
  {loading ? "..." : "Print/Export PDF - Air Cargo By Flight"}
</button>

            <button type="button" onClick={exportBolsStatusByFlight} disabled={loading}>
  {loading ? "..." : "Export PDF - BOL Status By Flight"}
</button>

            <button
              type="button"
              onClick={async () => {
                if (!manifestDeclaredVisible) {
                  await loadManifestDeclaredVsActual();
                  setManifestDeclaredVisible(true);
                } else {
                  setManifestDeclaredVisible(false);
                }
              }}
              disabled={loading}
            >
              {loading ? "..." : manifestDeclaredVisible ? "Hide Manifest Declared vs Actual" : "Show Manifest Declared vs Actual"}
            </button>



          </div>
        </div>
      )}
        {manifestDeclaredVisible && Array.isArray(manifestDeclared) && manifestDeclared.length > 0 && (
          <div className="card" style={{ marginTop: 12 }}>
            <h3>Manifest Declared vs Actual</h3>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Manifest ID</th>
                    <th>Voyage</th>
                    <th>Arrival</th>
                    <th>Declared</th>
                    <th>Actual</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {manifestDeclared.map((m) => {
                    const declared = m.declared_total_bols == null ? "" : m.declared_total_bols;
                    const actual = m.actual_total_bols || 0;
                    const isMatch = Number(declared) === Number(actual);
                    const isFullyDelivered = Number(m.remaining_bols || 0) === 0;
                    const bg = isFullyDelivered && isMatch ? "#dff0d8" : !isMatch ? "#f8d7da" : "transparent";
                    return (
                      <tr key={m.id} style={{ backgroundColor: bg }}>
                        <td>{m.id}</td>
                        <td>{m.voyage_number}</td>
                        <td>{m.date_of_arrival ? String(m.date_of_arrival).slice(0, 10) : ""}</td>
                        <td>{declared}</td>
                        <td>{actual}</td>
                        <td>{m.remaining_bols}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}

{/* ===== Tab: Stock ===== */}
{tab === "stock" && (
  <div className="card">
    <h3>Warehouse Reports</h3>

    {/* ===== Section 1: Current Stock ===== */}
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <h4 style={{ margin: 0 }}>Current Stock</h4>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button type="button" onClick={loadStock} disabled={loading}>
            {loading ? "..." : "Load Current Stock"}
          </button>

          <button
            type="button"
            onClick={() => {
              setStockItems([]);
              // لإخفاء الجدول بالكامل
              setMsg("Current Stock cleared ✅");
            }}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              borderRadius: 6,
              border: "1px solid #bbb",
              background: "#f5f5f5",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>
      </div>
      

      {Array.isArray(stockItems) && stockItems.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>AWB</th>
                <th>Current</th>
                <th>Added</th>
                <th>Withdrawn</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {stockItems.map((r) => (
                <tr key={r.bol_reference}>
                  <td>{r.bol_reference}</td>
                  <td>{formatNumber(r.current_qty, 0)}</td>
                  <td>{formatNumber(r.total_in, 0)}</td>
                  <td>{formatNumber(r.total_out, 0)}</td>
                  <td>{r.updated_at ? String(r.updated_at).slice(0, 19).replace("T", " ") : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {/* ===== Section 2: Stock Summary ===== */}
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <h4 style={{ margin: 0 }}>Stock Summary</h4>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button type="button" onClick={loadStockSummary} disabled={loading}>
            {loading ? "..." : "Load Summary"}
          </button>

          <button
            type="button"
            onClick={() => {
              setStockSummary(null);
              setMsg("Summary cleared ✅");
            }}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              borderRadius: 6,
              border: "1px solid #bbb",
              background: "#f5f5f5",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" onClick={loadStockByService} disabled={loading}>
          {loading ? "..." : "Load Stock By Service"}
        </button>

        <button
          type="button"
          onClick={() => { setStockByService(null); setMsg("Stock By Service cleared ✅"); }}
          style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, border: "1px solid #bbb", background: "#f5f5f5", cursor: "pointer" }}
        >
          Clear
        </button>
      </div>

      {stockByService && (
        <div style={{ marginTop: 12 }}>
          <h4>Stock By Service (Summary)</h4>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Total Qty</th>
                  <th>Estimated Value</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(stockByService.summary) && stockByService.summary.map((s) => (
                  <tr key={`${s.serviceId}_${s.serviceName}`}>
                    <td>{s.serviceName}</td>
                    <td>{formatNumber(s.totalQty, 0)}</td>
                    <td>{formatNumber(s.totalValue, 2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ fontWeight: "bold", textAlign: "right" }}>Total:</td>
                  <td style={{ fontWeight: "bold" }}>{formatNumber(stockByService.grandTotal, 2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <h4 style={{ marginTop: 12 }}>Detailed Items</h4>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>AWB</th>
                  <th>Goods Description</th>
                  <th>Departure Date</th>
                  <th>Arrival Date</th>
                  <th>Delivery Date</th>
                  <th>Current Qty</th>
                  <th>Gross Mass</th>
                  <th>Days</th>
                  <th>Service</th>
                  <th>Unit Price</th>
                  <th>Estimated Value</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(stockByService.items) && stockByService.items.map((it) => (
                  <tr key={it.bol_reference}>
                    <td>{it.bol_reference}</td>
                    <td>{it.goods_description}</td>
                    <td>{it.date_of_departure}</td>
                    <td>{it.date_of_arrival}</td>
                    <td>{it.delivery_date || ""}</td>
                    <td>{formatNumber(it.current_qty, 0)}</td>
                    <td>{formatNumber(it.gross_mass, 2)}</td>
                    <td>{it.days}</td>
                    <td>{it.service ? it.service.name : "Unassigned"}</td>
                    <td>{formatNumber(it.unitPrice, 2)}</td>
                    <td>{formatNumber(it.value, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {stockSummary && (
          <div className="grid grid-auto" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Current Qty (Total)</label>
              <input readOnly value={formatNumber(stockSummary.current_total ?? 0, 0)} />
            </div>
            <div className="field">
              <label>Total Added</label>
              <input readOnly value={formatNumber(stockSummary.total_added ?? 0, 0)} />
            </div>
            <div className="field">
              <label>Total Withdrawn</label>
              <input readOnly value={formatNumber(stockSummary.total_withdrawn ?? 0, 0)} />
            </div>
            <div className="field">
              <label>Withdrawn (From/To)</label>
              <input readOnly value={formatNumber(stockSummary.withdrawn_in_range ?? 0, 0)} />
            </div>
          </div>
      )}
    </div>

    {/* ===== Section 3: Stock Movements ===== */}
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <h4 style={{ margin: 0 }}>Stock Movements</h4>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={manifestFilterId}
            onChange={(e) => setManifestFilterId(e.target.value)}
            placeholder="Manifest ID"
            style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #ccc", width: 120 }}
          />
          <button type="button" onClick={loadStockMoves} disabled={loading}>
            {loading ? "..." : "Load Movements"}
          </button>

          <button
            type="button"
            onClick={() => {
              setStockMoves([]);
              setMsg("Movements cleared ✅");
            }}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              borderRadius: 6,
              border: "1px solid #bbb",
              background: "#f5f5f5",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {stockMoves.length > 0 && (
        <>
          <div style={{ marginTop: "20px" }}>
            <h4>Stock In (Additions)</h4>
            {inMoves.length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>AWB</th>
                      <th>Qty</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inMoves.map((m) => (
                      <tr key={m.id}>
                        <td>{m.created_at ? String(m.created_at).slice(0, 19).replace("T", " ") : ""}</td>
                        <td>{m.bol_reference}</td>
                        <td>{formatNumber(m.qty, 0)}</td>
                        <td>{m.note || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="hint-text">No IN movements found for the selected filters.</p>
            )}
          </div>

          <div style={{ marginTop: "20px" }}>
            <h4>Stock Out (Withdrawals)</h4>
            {outMoves.length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>AWB</th>
                      <th>Qty</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outMoves.map((m) => (
                      <tr key={m.id}>
                        <td>{m.created_at ? String(m.created_at).slice(0, 19).replace("T", " ") : ""}</td>
                        <td>{m.bol_reference}</td>
                        <td>{formatNumber(m.qty, 0)}</td>
                        <td>{m.note || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="hint-text">No OUT movements found for the selected filters.</p>
            )}
          </div>
        </>
      )}
    </div>
  </div>
)}

{tab === "financial" && (
  <div className="card">
    <h3>Stock Value Report (Detailed)</h3>
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
      <button type="button" onClick={loadFinancialReport} disabled={loading}>
        {loading ? "..." : "Load Financial Report"}
      </button>
       <button
            type="button"
            onClick={() => {
              setFinancialReport(null);
              setMsg("Report cleared ✅");
            }}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              borderRadius: 6,
              border: "1px solid #bbb",
              background: "#f5f5f5",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
    </div>

    {financialReport && financialReport.report && (
      <div>
        <div style={{ marginBottom: 15, fontSize: "1.1rem", fontWeight: "bold", textAlign: "right" }}>
          Grand Total: {formatNumber(financialReport.grand_total, 2)}
        </div>

        {financialReport.report.map((op) => (
          <div key={op.operator} className="card" style={{ marginBottom: 20, border: "1px solid #e0e0e0" }}>
            <div style={{ backgroundColor: "#f5f5f5", padding: "10px", fontWeight: "bold", fontSize: "1rem", borderBottom: "1px solid #ddd" }}>
              Operator: {op.operator} <span style={{ float: "right" }}>Total: {formatNumber(op.total_value, 2)}</span>
            </div>

            {op.manifests.map((m) => (
              <div key={m.id} style={{ padding: "10px", borderBottom: "1px dashed #eee" }}>
                <div style={{ marginBottom: 8, fontSize: "0.9rem", color: "#555" }}>
                  <strong>Manifest:</strong> {m.id} &nbsp;|&nbsp; <strong>Voyage:</strong> {m.voyage_number} &nbsp;|&nbsp; <strong>Arrival:</strong> {String(m.date_of_arrival).slice(0, 10)} &nbsp;|&nbsp; <strong>Manifest Total:</strong> {formatNumber(m.total_value, 2)}
                </div>
                <div className="table-wrap">
                  <table className="data-table" style={{ fontSize: "0.85rem" }}>
                    <thead>
                      <tr>
                        <th>AWB No.</th>
                        <th>Arrival Date</th>
                        <th>Calc. Date (Today)</th>
                        <th>Goods Description</th>
                        <th>Weight</th>
                        <th>Days</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.bols.map((b) => (
                        <tr key={b.id}>
                          <td>{b.bol_reference}</td>
                          <td>{String(m.date_of_arrival).slice(0, 10)}</td>
                          <td>{new Date().toISOString().slice(0, 10)}</td>
                          <td>{b.goods_description}</td>
                          <td>{formatNumber(b.gross_mass, 2)}</td>
                          <td>{b.storage_days}</td>
                          <td>{formatNumber(b.value, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    )}
  </div>
)}

{tab === "delivered" && (
  <div className="card">
    <h3>Delivered Stock Report (Detailed)</h3>
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
      <button type="button" onClick={loadDeliveredReport} disabled={loading}>
        {loading ? "..." : "Load Delivered Report"}
      </button>
      <button type="button" onClick={loadDeliveredDetailed} disabled={loading}>
        {loading ? "..." : "Load Detailed Delivered Report"}
      </button>
       <button
            type="button"
            onClick={() => {
              setDeliveredReport(null);
              setDeliveredDetailed(null);
              setMsg("Report cleared ✅");
            }}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              borderRadius: 6,
              border: "1px solid #bbb",
              background: "#f5f5f5",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
    </div>

    {deliveredReport && deliveredReport.report && (
      <div>
        <div style={{ marginBottom: 15, fontSize: "1.1rem", fontWeight: "bold", textAlign: "right" }}>
          Grand Total: {formatNumber(deliveredReport.grand_total, 2)}
        </div>

        {deliveredReport.report.map((op) => (
          <div key={op.operator} className="card" style={{ marginBottom: 20, border: "1px solid #e0e0e0" }}>
            <div style={{ backgroundColor: "#f5f5f5", padding: "10px", fontWeight: "bold", fontSize: "1rem", borderBottom: "1px solid #ddd" }}>
              Operator: {op.operator} <span style={{ float: "right" }}>Total: {formatNumber(op.total_revenue, 2)}</span>
            </div>

            {op.manifests.map((m) => (
              <div key={m.id} style={{ padding: "10px", borderBottom: "1px dashed #eee" }}>
                <div style={{ marginBottom: 8, fontSize: "0.9rem", color: "#555" }}>
                  <strong>Manifest:</strong> {m.id} &nbsp;|&nbsp; <strong>Voyage:</strong> {m.voyage_number} &nbsp;|&nbsp; <strong>Arrival:</strong> {String(m.date_of_arrival).slice(0, 10)} &nbsp;|&nbsp; <strong>Manifest Total:</strong> {formatNumber(m.total_revenue, 2)}
                </div>
                <div className="table-wrap">
                  <table className="data-table" style={{ fontSize: "0.85rem" }}>
                    <thead>
                      <tr>
                        <th>AWB No.</th>
                        <th>Delivery Date</th>
                        <th>Goods Description</th>
                        <th>Weight</th>
                        <th>Day Subtracted?</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.bols.map((b) => (
                        <tr key={b.id}>
                          <td>{b.bol_reference}</td>
                          <td>{String(b.delivery_date).slice(0, 10)}</td>
                          <td>{b.goods_description}</td>
                          <td>{formatNumber(b.gross_mass, 2)}</td>
                          <td>{b.day_subtracted ? "Yes" : "No"}</td>
                          <td>{formatNumber(b.revenue, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    )}

    {deliveredDetailed && (
      <div style={{ marginTop: 20 }}>
        <h4>Detailed Delivered BOLs</h4>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>AWB</th>
                <th>Goods Description</th>
                <th>Departure Date</th>
                <th>Arrival Date</th>
                <th>Delivery Date</th>
                <th>Qty</th>
                <th>Gross Mass</th>
                <th>Days</th>
                <th>Service</th>
                <th>Unit Price</th>
                <th>Estimated Value</th>
              </tr>
            </thead>
            <tbody>
              {deliveredDetailed.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.bol_reference}</td>
                  <td>{row.goods_description}</td>
                  <td>{row.date_of_departure}</td>
                  <td>{row.date_of_arrival}</td>
                  <td>{row.delivery_date}</td>
                  <td>{formatNumber(row.qty, 0)}</td>
                  <td>{formatNumber(row.gross_mass, 2)}</td>
                  <td>{row.actual_days}</td>
                  <td>{row.service_name}</td>
                  <td>{formatNumber(row.unit_price, 2)}</td>
                  <td>{formatNumber(row.revenue, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
)}

{tab === "reprint" && (
  <div className="card">
    <h3>Re-print Documents</h3>
    <div className="grid grid-auto">
      <div className="field" style={{ gridColumn: "1 / -1" }}>
        <button type="button" onClick={handleReprintSearch} disabled={loading}>
          {loading ? "Searching..." : "Search AWB"}
        </button>
      </div>
    </div>

    {reprintResult && (
      <div style={{ marginTop: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {Array.isArray(reprintResult) ? (
          reprintResult.map((item, idx) => {
            const { bol, header } = item;
            return (
              <div className="card" key={bol.id || idx}>
                <h4 style={{ margin: "0 0 0.5rem 0" }}>AWB: {bol.bol_reference}</h4>
                <div className="grid grid-auto small-gap" style={{ fontSize: "0.9rem" }}>
                  <div><strong>Flight:</strong> {header.flight_no || header.voyage_number}</div>
                  <div><strong>Arrival:</strong> {header.date_of_arrival ? String(header.date_of_arrival).slice(0, 10) : ""}</div>
                  <div><strong>Consignee:</strong> {bol.consignee_name}</div>
                  <div><strong>Status:</strong> {Number(bol.is_delivered) === 1 ? 'Delivered ✅' : Number(bol.is_do_issued) === 1 ? 'DO Issued 📜' : 'Pending ⏳'}</div>
                  {Number(bol.is_delivered) === 1 && <div><strong>Delivery Date:</strong> {formatDateToYMD_forReprint(bol.delivery_date)}</div>}
                </div>
                
                <div style={{ marginTop: '1rem', display: 'flex', gap: '10px', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                  {Number(bol.is_do_issued) === 1 && (
                    <button type="button" onClick={() => handleReprintDO(item)}>
                      Re-print DO & Receipt
                    </button>
                  )}
                  {Number(bol.is_delivered) === 1 && (
                    <button type="button" onClick={() => handleReprintInvoice(item)}>
                      Re-print Invoice
                    </button>
                  )}
                  {Number(bol.is_do_issued) !== 1 && Number(bol.is_delivered) !== 1 && (
                    <p className="hint-text">No documents available for re-printing for this AWB.</p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="message error">Error loading data.</div>
        )}
      </div>
    )}
  </div>
)}


    </div>
  );
}